import { Component, effect, inject, signal } from '@angular/core';
import { ChildrenOutletContexts, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavbarComponent } from './shared/components/navbar/navbar.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { CartPanelComponent } from './shared/components/cart-panel/cart-panel.component';
import { NotificationsPanelComponent } from './shared/components/notifications-panel/notifications-panel.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { SplashScreenComponent } from './shared/components/splash-screen/splash-screen.component';
import { PurchaseSuccessModalComponent } from './shared/components/purchase-success-modal/purchase-success-modal.component';
import { AuthService } from './core/services/auth.service';
import { AuthPromptService } from './core/services/auth-prompt.service';
import { CartService } from './core/services/cart.service';
import { BookService } from './core/services/book.service';
import { WishlistService } from './core/services/wishlist.service';
import { NotificationsService } from './core/services/notifications.service';
import { QuoteService } from './core/services/quote.service';
import { VisitorService } from './core/services/visitor.service';
import { LiteraryQuote } from './core/models/literary-quote.model';
import { routeFadeSlide } from './shared/animations';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    NavbarComponent,
    FooterComponent,
    CartPanelComponent,
    NotificationsPanelComponent,
    ToastComponent,
    SplashScreenComponent,
    PurchaseSuccessModalComponent
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
  animations: [routeFadeSlide]
})
export class App {
  readonly cartOpen = signal(false);
  readonly notificationsOpen = signal(false);
  readonly showMainChrome = signal(true);
  readonly authPrompt = inject(AuthPromptService);
  readonly splashMounted = signal(true);
  readonly splashActive = signal(true);
  readonly splashQuote: LiteraryQuote;
  private readonly contexts = inject(ChildrenOutletContexts);

  private splashDismissScheduled = false;

  constructor(
    public readonly authService: AuthService,
    private readonly cartService: CartService,
    private readonly bookService: BookService,
    private readonly wishlistService: WishlistService,
    private readonly notificationsService: NotificationsService,
    private readonly quoteService: QuoteService,
    private readonly visitorService: VisitorService,
    private readonly router: Router
  ) {
    this.splashQuote = this.quoteService.randomQuote();
    this.bootstrapSplash();

    // Refresh role/profile from API so admin promotions apply after reload
    if (this.authService.currentUser()) {
      this.authService.refreshCurrentUser().subscribe();
    }

    const updateChrome = () => {
      const path = this.router.url.split(/[?#]/)[0];
      const hide =
        /^\/magazine\/[^/]+$/.test(path) ||
        path === '/landing' ||
        path === '/login' ||
        path === '/register' ||
        path === '/admin' ||
        path.startsWith('/admin/');
      this.showMainChrome.set(!hide);
    };
    updateChrome();
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(updateChrome);

    effect(() => {
      const user = this.authService.currentUser();
      // Persist visit in DB once per session identity (Guest or logged-in user).
      this.visitorService.trackVisit(this.router.url.split(/[?#]/)[0] || '/');
      if (user) {
        this.cartService.loadCart(user.UsrId);
        this.wishlistService.load();
        this.notificationsService.load();
      } else {
        this.cartService.cartItems.set([]);
      }
    });
  }

  openAuthPage(): void {
    // Route to the full login screen (no popup)
    this.authPrompt.requestAuth(this.router.url);
  }

  openCartPanel(): void {
    this.notificationsOpen.set(false);
    this.cartOpen.set(true);
  }

  closeCartPanel(): void {
    this.cartOpen.set(false);
  }

  openNotificationsPanel(): void {
    this.cartOpen.set(false);
    this.notificationsOpen.set(true);
  }

  closeNotificationsPanel(): void {
    this.notificationsOpen.set(false);
  }

  onSplashClosed(): void {
    this.splashMounted.set(false);
  }

  routeAnimationData(): string {
    return this.contexts.getContext('primary')?.route?.snapshot?.routeConfig?.path ?? '';
  }

  private bootstrapSplash(): void {
    const startedAt = Date.now();
    const minDisplayMs = 3500;
    const maxDisplayMs = 6000;

    const scheduleDismiss = () => {
      if (this.splashDismissScheduled) return;
      this.splashDismissScheduled = true;
      const wait = Math.max(0, minDisplayMs - (Date.now() - startedAt));
      setTimeout(() => this.splashActive.set(false), wait);
    };

    setTimeout(() => scheduleDismiss(), maxDisplayMs);

    if (this.bookService.books().length > 0) {
      scheduleDismiss();
      return;
    }

    this.bookService.loadBooks();

    effect(() => {
      const loading = this.bookService.loading();
      const hasBooks = this.bookService.books().length > 0;
      const hasError = !!this.bookService.error();
      if (!loading && (hasBooks || hasError)) {
        scheduleDismiss();
      }
    });
  }
}
